import React from 'react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import PostAddIcon from '@mui/icons-material/PostAdd';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import CollectionsIcon from '@mui/icons-material/Collections';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useRequireSignIn } from '../../components/auth/useRequireSignIn';
import { hexWithAlpha, themeGradient, TOTAL_OPERATION_COUNT } from './labCapabilities';

interface QuickAction {
  label: string;
  icon: React.ReactElement;
  to: string;
  /** Hidden when the backing lab is gated off (SMA-13). */
  labId?: string;
}

interface Props {
  /** Lab ids the backend currently exposes; undefined while the catalog loads. */
  enabledLabIds?: string[];
}

/**
 * Landing hero — the "start something" surface.
 *
 * The gradient is built from the active theme's own primary/secondary tokens
 * (see `themeGradient`), so each of the three themes renders in its own
 * identity instead of a hardcoded brand wash. The panel keeps a solid
 * `background.paper` base underneath so text contrast holds when a theme's
 * palette is light.
 */
export const DashboardHero: React.FC<Props> = ({ enabledLabIds }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, isAuthenticated } = useAuth();
  const { promptSignIn } = useRequireSignIn();

  const firstName = displayName(user);

  const actions: QuickAction[] = [
    { label: 'New image', icon: <AddPhotoAlternateIcon />, to: '/labs/image-lab', labId: 'image-lab' },
    { label: 'New document', icon: <PostAddIcon />, to: '/labs/writer-lab', labId: 'writer-lab' },
    { label: 'New video', icon: <VideoCallIcon />, to: '/labs/animation-lab', labId: 'animation-lab' },
    { label: 'Asset gallery', icon: <CollectionsIcon />, to: '/assets' },
  ];

  // Optimistic while the catalog is still loading, matching LabShowcase —
  // otherwise every action flashes out of existence on first paint.
  const visible = actions.filter(
    a => !a.labId || !enabledLabIds || enabledLabIds.includes(a.labId),
  );

  return (
    <Box
      aria-label="Welcome"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        px: { xs: 3, md: 5 },
        py: { xs: 4, md: 6 },
      }}
    >
      {/* Gradient wash + a soft radial bloom in the top-right, so the panel has
          depth without competing with the text sitting on top of it. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: themeGradient(theme),
          opacity: theme.palette.mode === 'dark' ? 0.28 : 0.16,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -160,
          right: -120,
          width: 460,
          height: 460,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${hexWithAlpha(theme.palette.secondary.main, 0.45)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        sx={{ position: 'relative', alignItems: { md: 'center' }, justifyContent: 'space-between' }}
      >
        <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
        <Stack spacing={1.25}>
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{ fontSize: { xs: '1.9rem', md: '2.6rem' }, letterSpacing: '-0.02em' }}
          >
            {/* "Welcome back" only makes sense for someone who has been here
                before. A first-time visitor gets an invitation instead. */}
            {isAuthenticated
              ? firstName
                ? `Welcome back, ${firstName}`
                : 'Welcome back'
              : 'Make something'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 620 }}>
            {isAuthenticated
              ? `${TOTAL_OPERATION_COUNT} creative operations across every lab. Pick up where you left off, or start something new.`
              : `${TOTAL_OPERATION_COUNT} creative operations across image, writing and motion. Explore everything without an account — sign in when you want to generate and keep your work.`}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          {!isAuthenticated && (
            <Button
              variant="contained"
              size="large"
              onClick={promptSignIn}
              sx={{ borderRadius: 999, px: 2.5, textTransform: 'none', fontWeight: 600 }}
            >
              Sign in or create account
            </Button>
          )}
          {visible.map((a, i) => (
            <Button
              key={a.to}
              // Lead action is filled; the rest read as secondary so the eye
              // lands on "New image" first.
              variant={i === 0 && isAuthenticated ? 'contained' : 'outlined'}
              size="large"
              startIcon={a.icon}
              onClick={() => navigate(a.to)}
              sx={{
                borderRadius: 999,
                px: 2.5,
                textTransform: 'none',
                fontWeight: 600,
                ...((i !== 0 || !isAuthenticated) && {
                  bgcolor: 'background.paper',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }),
              }}
            >
              {a.label}
            </Button>
          ))}
        </Stack>
        </Stack>

        {/* The Coral mark, sitting inside the radial bloom so the bloom reads as
            its halo. Decorative: the heading already names the product, so an
            alt here would make a screen reader announce the brand twice. Hidden
            below md, where it would push the actions off the fold. */}
        <Box
          component="img"
          src="/images/coral-mark-512.png"
          alt=""
          aria-hidden
          sx={{
            display: { xs: 'none', md: 'block' },
            width: { md: 148, lg: 184 },
            height: 'auto',
            flexShrink: 0,
            ml: 3,
          }}
        />
      </Stack>
    </Box>
  );
};

/**
 * First name for the greeting. Falls back through the shapes a central-SSO
 * token can carry, then to nothing — the hero reads fine without a name.
 */
function displayName(user: { name?: string; email?: string } | null | undefined): string {
  const raw = user?.name?.trim() || user?.email?.split('@')[0] || '';
  if (!raw) return '';
  const first = raw.split(/[\s.]+/)[0];
  return first.charAt(0).toUpperCase() + first.slice(1);
}
